import type {
  MediaDevice,
  MediaDeviceGroup,
} from 'features/media-devices/interfaces';
import type { SelectItem } from 'features/ui/elements';

interface BootstrapMediaDevicesArgs {
  devices: MediaDeviceInfo[];
  storedDevices: Partial<MediaDeviceGroup>;
}

interface BootstrapMediaDevicesResult {
  cameraList: SelectItem[];
  microphoneList: SelectItem[];
  speakerList: SelectItem[];
  selected: {
    camera: MediaDevice;
    microphone: MediaDevice;
    speaker: MediaDevice;
  };
}

const isStoredDeviceExists = (
  storedDeviceId: string | null | undefined,
  list: SelectItem[]
) => {
  if (!storedDeviceId) {
    return false;
  }

  return list.some(({ value }) => value === storedDeviceId);
};

const getSelectedDevice = (
  storedDeviceId: string | null | undefined,
  list: SelectItem[]
): MediaDevice => {
  const [firstDevice] = list;

  return isStoredDeviceExists(storedDeviceId, list)
    ? (storedDeviceId ?? null)
    : (firstDevice?.value ?? null);
};

const getSelectedCamera = (
  storedCameraId: string | null | undefined,
  list: SelectItem[]
): MediaDevice => {
  if (!storedCameraId) {
    return getSelectedDevice(storedCameraId, list);
  }

  return isStoredDeviceExists(storedCameraId, list) ? storedCameraId : null;
};

export const bootstrapMediaDevices = ({
  devices,
  storedDevices,
}: BootstrapMediaDevicesArgs): BootstrapMediaDevicesResult => {
  const lists = devices.reduce<{
    cameraList: SelectItem[];
    microphoneList: SelectItem[];
    speakerList: SelectItem[];
  }>(
    (acc, { kind, label, deviceId }) => {
      const item = {
        value: deviceId,
        text: label,
      };

      if (kind === 'videoinput') {
        acc.cameraList.push(item);
      }
      if (kind === 'audioinput') {
        acc.microphoneList.push(item);
      }
      if (kind === 'audiooutput') {
        acc.speakerList.push(item);
      }

      return acc;
    },
    {
      cameraList: [],
      microphoneList: [],
      speakerList: [],
    }
  );

  return {
    ...lists,
    selected: {
      camera: getSelectedCamera(storedDevices.camera, lists.cameraList),
      microphone: getSelectedDevice(
        storedDevices.microphone,
        lists.microphoneList
      ),
      speaker: getSelectedDevice(storedDevices.speaker, lists.speakerList),
    },
  };
};
